import type { RouteLocationNormalized } from 'vue-router';

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized) => {
  const refreshCookieAdmin = useCookie('refreshTokenAdmin');
  const hasTokenCookie = useCookie('hasTokenAdmin');

  const localePath = useLocalePath();
  const userStore = useUserAdminStore();
  const config = useRuntimeConfig();
  const siteType = config.public.frontMode;

  function decodeJwt(token: string): any | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = parts[1];
      let decodedPayload: string;
      if (import.meta.server) {
        decodedPayload = Buffer.from(payload, 'base64').toString('utf8');
      } else {
        decodedPayload = atob(payload);
      }
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Erreur lors du décodage du JWT:', error);
      return null;
    }
  }

  async function resetAdminAuthConfig() {
    try {
      await fetch('/api/admin/auth/reset-config', { method: 'POST' });
      console.info('[Auth] Reset auth configuration.');
    } catch (error) {
      console.error('[Auth] Failed to reset auth config:', error);
    }
  }

  async function checkAdminAuthOnServer() {
    const validateJwt = (token: string) => {
      if (!token) return false;
      const decoded = decodeJwt(token);
      console.log('🟡 [MIDDLEWARE] decoded:', decoded);
      console.log('🟡 [MIDDLEWARE] exp:', decoded?.exp);
      console.log('🟡 [MIDDLEWARE] now:', Date.now() / 1000);
      console.log('🟡 [MIDDLEWARE] is expired:', decoded?.exp && Date.now() / 1000 < decoded.exp);
      return decoded?.exp && Date.now() / 1000 < decoded.exp;
    };

    // Récupérer l'accessToken depuis sessionStorage (côté client uniquement)
    let accessToken: string | null = null;
    if (import.meta.client) {
      accessToken = sessionStorage.getItem('tokenAdmin');
    }

    console.log('🟡 [MIDDLEWARE] refreshTokenAdmin (cookie):', refreshCookieAdmin.value ? '✅ Présent' : '❌ Absent');
    console.log('🟡 [MIDDLEWARE] hasTokenAdmin (cookie):', hasTokenCookie.value);
    if (import.meta.client) {
      console.log('🟡 [MIDDLEWARE] tokenAdmin (sessionStorage):', accessToken ? '✅ Présent' : '❌ Absent');
    }

    // Validation côté client avec l'accessToken
    if (import.meta.client && accessToken && validateJwt(accessToken)) {
      console.log('🟢 [MIDDLEWARE] JWT admin valide (sessionStorage)');
      return true;
    }

    // Validation côté serveur avec le flag hasToken
    if (import.meta.server && hasTokenCookie.value === 'true') {
      console.log('🟢 [MIDDLEWARE] Token admin présumé valide (serveur)');
      return true; // On assume que le token est valide côté serveur
    }

    // Logique de refresh token si le JWT est expiré mais qu'on a un refresh token
    if (import.meta.client && refreshCookieAdmin.value) {
      console.log('🔄 [MIDDLEWARE] Tentative de refresh du token admin...');
      
      try {
        const refreshResponse = await fetch('/api/admin/auth/refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            jwt: accessToken,
            refreshToken: refreshCookieAdmin.value
          })
        });

        const refreshData = await refreshResponse.json();

        if (refreshData?.isAuthenticated && refreshData.token) {
          console.log('✅ [MIDDLEWARE] Refresh token admin réussi');
          
          // Mettre à jour le token dans sessionStorage
          sessionStorage.setItem('tokenAdmin', refreshData.token);
          
          // Mettre à jour le refresh token si fourni
          if (refreshData.refreshToken) {
            refreshCookieAdmin.value = refreshData.refreshToken;
          }
          
          // Mettre à jour le flag hasToken
          hasTokenCookie.value = 'true';
          
          return true;
        } else {
          console.log('❌ [MIDDLEWARE] Refresh token admin échoué');
        }
      } catch (error) {
        console.error('🔴 [MIDDLEWARE] Erreur lors du refresh token admin:', error);
      }
    }

    console.log('🔴 [MIDDLEWARE] JWT admin non valide ou absent');
    return false;
  }

  const isAdminAuthenticated = await checkAdminAuthOnServer();
  userStore.isAdminAuthenticated = isAdminAuthenticated;
  useCookie('isAdminAuthenticated').value = isAdminAuthenticated;
  
  
  // Redirection vers la page de connexion admin si non authentifié
  if (!isAdminAuthenticated) {
    // Vérifier si on n'est pas déjà sur une page d'auth admin
    // Note: avec i18n, le path peut avoir un préfixe de locale (ex: /en-GB/auth/login)
    // On utilise une regex pour ignorer le préfixe de locale optionnel
    const pathWithoutLocale = to.path.replace(/^\/(fr-FR|en-GB)/, '');
    const isAuthAdminPage = pathWithoutLocale.startsWith('/auth') || 
                            pathWithoutLocale === '/login' || 
                            pathWithoutLocale.startsWith('/customer/requests');
    console.log('🔴 [MIDDLEWARE] isAuthAdminPage:', isAuthAdminPage);
    console.log('🔴 [MIDDLEWARE] siteType:', siteType);
    if (!isAuthAdminPage && siteType === 'admin') {
      console.log('🔄 [MIDDLEWARE] Redirection vers la page de connexion admin');
      
      // Reset des cookies d'authentification
      await resetAdminAuthConfig();
      refreshCookieAdmin.value = null;
      hasTokenCookie.value = null;
      
      // Nettoyage sessionStorage côté client
      if (import.meta.client) {
        sessionStorage.removeItem('tokenAdmin');
      }
      
      return navigateTo(localePath('/auth/login'));
    }
  }
});
